import React, { useCallback, useEffect, useRef, useState } from 'react'
import { environment } from '../../environments/environment';
import api from '../../api/api';
import { toast } from 'react-toastify';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { IoSaveOutline, IoImageOutline } from "react-icons/io5";
import { cn } from '../../utility/function';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { MdDragIndicator } from "react-icons/md";


interface ProductProps {
  sort_id: number;
  media_url: string;
  product_title: string;
  product_description: string;
  uuid: string;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const AdminSortProducts = () => {
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  // const [currentPage, setCurrentPage] = useState(1);


  const fetchAllProducts = async () => {
    try {
      const response = await api.get(`${environment.baseUrl}/admin/product-order`);

      if (response.data.success) {
        const apiResponse = response.data.data.products || [];
        const productImages = response.data.data.product_images || {};


        const highestSortId = apiResponse.reduce(
          (max: number, product: any) => {
            return product.sort_id > max ? product.sort_id : max;
          }
          , 0
        );

        let nextSortId = highestSortId + 1;

        const formattedResponse = apiResponse.map((product: any) => {
          if (product.sort_id === undefined || product.sort_id === 0) {
            product.sort_id = nextSortId++;
          }

          // Get the first image URL for the campaign
          const firstImageUrl =
            productImages[product.uuid]?.[0]?.media_url || "";

          return {
            ...product,
            sort_id: product.sort_id,
            media_url: firstImageUrl,
          };
        });

        const sortedResponse = formattedResponse.sort((a: ProductProps, b: ProductProps) => a.sort_id - b.sort_id)

        setProducts(sortedResponse);

      }
    } catch(error) {
      console.error(error)
    }
  }

  const saveSortOrder = async () => {
    const payload = {
      products: products.map(({ uuid, sort_id }) => ({uuid, sort_id}))
    }
    try {
      const response = await api.put(`${environment.baseUrl}/admin/product-order`, payload)


      if(response.data.success) {
        toast.success("Products order saved successfully!")
      }

    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchAllProducts()
  }, [])


  const moveProduct = useCallback((dragIndex: number, hoverIndex: number) => {
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      const draggedProduct = updatedProducts[dragIndex];
      updatedProducts.splice(dragIndex, 1);
      updatedProducts.splice(hoverIndex, 0, draggedProduct);
      return updatedProducts.map((product, index) => ({
        ...product,
        sort_id: index + 1, // Update sort_id based on new index
      }));

    })

  }
  , [])


  const filteredProducts = products.filter((product) =>
    product.product_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-between mb-8">
                  <h1 className="text-3xl font-bold text-gray-800">Sort Products</h1>
        
                  <button
                    onClick={saveSortOrder}
                    className={cn(
                      "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      "disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
                      "bg-[#0E203F] text-white hover:bg-[#0E203F]/90",
                      "h-10 py-2 px-4"
                    )}
                  >
                    <IoSaveOutline className="w-4 h-4 mr-2" />
                    Save Order
                  </button>
                </div>

        <div className="mb-6">
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>

        <div className="space-y-4">
          {filteredProducts.map((product, index) => (
            <DraggableProductCard
              key={product.uuid}
              product={product}
              index={index}
              moveProduct={moveProduct}
            />
          ))}
        </div>
      </div>
      </DndProvider>
  )
}


const DraggableProductCard = ({ product, index, moveProduct }: {
  product: ProductProps;
  index: number;
  moveProduct: (dragIndex: number, hoverIndex: number) => void;
}) => {

  const ref = useRef<HTMLDivElement>(null);

  const cloudinaryImageUrl = product.media_url.includes("cloudinary.com")
    ? product.media_url.replace(
        "/upload/",
        "/upload/w_90,h_90,c_fill,ar_1:1,f_auto,q_auto:best/"
      )
    : product.media_url;

  
    const [{ handlerId }, drop] = useDrop<
      DragItem,
      void,
      { handlerId: string | null }
    >({
      accept: "product",
      collect(monitor: DropTargetMonitor) {
        return {
          handlerId: monitor.getHandlerId() as string | null,
        };
      },
      hover(item: DragItem, monitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) {
          return;
        }
        moveProduct(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });
  
    const [{ isDragging }, drag, preview] = useDrag({
      type: "product",
      item: () => ({ id: product.uuid, index }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });
  
    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));
  
    return (
      <Card ref={preview} style={{ opacity }} data-handler-id={handlerId}>
        <CardContent className="flex items-center space-x-4 p-4">
          <div ref={ref} className="cursor-move">
            <MdDragIndicator size={20} />
          </div>
          <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
            {product.media_url ? (
              <img
                // src={product.image_url}
                src={cloudinaryImageUrl}
                alt={product.product_title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <IoImageOutline className="text-gray-400" size={32} />
              </div>
            )}
          </div>
          <div className="flex-grow">
            <h2 className="text-xl font-semibold mb-1">
              {product.product_title}
            </h2>
            <p className="text-sm text-gray-600 mb-2">
              {product.product_description}
            </p>
            {/* <Badge variant="secondary">Order: {campaign.sort_id}</Badge> */}
          </div>
        </CardContent>
      </Card>
    );
  
}

export default AdminSortProducts